export function sanitizeDecimalInput(value: string): string {
  const sanitizedValue = value.replace(/[^0-9.,]/g, "");
  let hasDecimalSeparator = false;
  let result = "";

  for (const char of sanitizedValue) {
    if (char === "." || char === ",") {
      if (hasDecimalSeparator) {
        continue;
      }
      hasDecimalSeparator = true;
    }

    result += char;
  }

  return result;
}

export function parseDecimalNumber(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== "string") return undefined;

  const normalizedValue = value.trim().replace(/,/g, ".");
  if (normalizedValue === "" || normalizedValue === ".") {
    return undefined;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

/**
 * The Portuguese money format, `1 234,50 €`.
 *
 * The same body is declared locally in `BudgetCard` and `ProductsStepContent`;
 * new call sites should use this one.
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

/** A plain count with the Portuguese thousands separator. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat("pt-PT").format(value);
}

/**
 * A rate the API already expresses as a percentage — `83.33` is 83,33 %, not
 * 8333 %. This only formats it; it never divides.
 */
export function formatPercent(value: number, digits = 1): string {
  return `${new Intl.NumberFormat("pt-PT", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)} %`;
}

/**
 * A duration given in minutes, in the largest unit that still reads as a
 * duration.
 *
 * The API answers averages like `3303`, and "3303,00 min" is not a number
 * anyone can act on — it is two days and seven hours. Minutes are kept below
 * an hour, hours and minutes below a day, days and hours above it.
 */
export function formatMinutes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 min";

  const totalMinutes = Math.round(value);
  if (totalMinutes < 60) return `${formatCount(totalMinutes)} min`;

  if (totalMinutes < 60 * 24) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
  }

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  return hours === 0
    ? `${formatCount(days)} d`
    : `${formatCount(days)} d ${hours} h`;
}

/** A whole number of days, singular below two. */
export function formatDays(value: number): string {
  const days = Math.round(value);
  return days === 1 ? "1 dia" : `${formatCount(days)} dias`;
}
